import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { X, Send } from "lucide-react";
import toast from "react-hot-toast";
import moment from "moment";
import { updateLead } from "../../../api/lead";
import { getAgents } from "../../../api/agent";
import InfiniteScrollSelect from "../../../components/common/InfiniteScrollSelect";

/**
 * Hand a lead to an agent, with a note.
 *
 * Christian Gravias, Monday 12497182031, 22 Aug 2026: "Please also add a button
 * which allows me to write a note to the agent before I send the lead over to
 * them."
 *
 * Christian works the social-media inbox centrally and then hands each lead to
 * whoever should chase it. Until now that was a bare dropdown: the agent got the
 * standard "new lead assigned to you" email and none of the context Christian had
 * from looking at the campaign it came off. The note is the context.
 *
 * Two things happen on Send, deliberately in one request:
 *   1. `handover_note` rides along with the assignment. It is not stored on the
 *      Lead — the backend puts it at the TOP of the assignment email and on the
 *      in-app bell entry, then drops it. It belongs to this hand-over.
 *   2. The same words are appended to `agent_notes`, the append-only thread the
 *      Notes button shows, so there is a permanent record of what was said and
 *      who said it. Append-only, no edit and no delete, same rule as every other
 *      note thread in the CRM.
 *
 * The email only fires when the lead actually moves to a DIFFERENT agent, which
 * is the existing behaviour of the post_save alert and is why the note box is
 * disabled once the picked agent is the one already on the lead.
 */

// The display stamp the existing thread is written with, kept byte-identical so
// notes written here render the same in LeadNotesModal and the Buyer Enquiries
// modal, which print note.timestamp straight out.
const STAMP_FORMAT = "D/M/YYYY h:mm a";

const AssignLeadModal = ({ isOpen, lead, agents = [], initialAgentId = "", onClose, onAssigned }) => {
  const { user } = useSelector((state) => state.auth || {});
  const [agentId, setAgentId] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setAgentId(initialAgentId ? String(initialAgentId) : "");
    setNote("");
  }, [isOpen, initialAgentId, lead?.id]);

  // Server-side agent search for the picker (agents only).
  const fetchAgentOptions = useCallback(async (page, pageSize, search = "") => {
    const res = await getAgents({ page, page_size: pageSize, search, role: "agent" });
    const data = res?.data ?? {};
    return {
      results: data.results ?? (Array.isArray(data) ? data : []),
      next: Boolean(data.next),
    };
  }, []);

  if (!isOpen || !lead) return null;

  const leadName = lead.name || lead.full_name || lead.email || "No Name";
  const currentAgentId = lead.agent ? String(lead.agent) : "";
  const agentName = agents.find((a) => String(a.id) === String(agentId))?.name || "";
  // Same agent as the lead already has, so nothing is being sent anywhere and
  // the backend will not email. Say so rather than letting a note vanish.
  const isSameAgent = !!agentId && agentId === currentAgentId;
  const isUnassigning = !agentId;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    const content = note.trim();
    const payload = { agent: agentId || null };

    if (content && !isUnassigning) {
      payload.handover_note = content;
      // Send ONLY the new note, never the whole thread. Building the array from
      // `lead.agent_notes` meant handing a lead over deleted whatever the agent
      // had written since this row was last loaded, and the agent's own save did
      // the same to this note (Christian Gravias, 24 Aug 2026: "even they save
      // the notes it disappear"). The backend appends to what is really stored -
      // lead_detail_views._append_agent_note.
      payload.append_agent_note = {
        timestamp: moment().format(STAMP_FORMAT),
        created_at: moment().toISOString(),
        author: user?.name || user?.email || "Agent",
        content: agentName ? `Sent to ${agentName}: ${content}` : content,
      };
    }

    setSaving(true);
    try {
      const res = await updateLead(lead.id, payload);
      toast.success(
        isUnassigning
          ? "Lead unassigned."
          : content && !isSameAgent
            ? `Lead sent to ${agentName || "the agent"} with your note.`
            : `Lead assigned to ${agentName || "the agent"}.`
      );
      onAssigned &&
        onAssigned({
          ...lead,
          agent: agentId || null,
          agent_name: agentName || "N/A",
          // The thread as the database has it, so the row's note count and
          // "Last note:" line are right even if somebody else just wrote one.
          agent_notes: Array.isArray(res?.data?.agent_notes)
            ? res.data.agent_notes
            : lead.agent_notes,
        });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to assign the lead.");
    } finally {
      setSaving(false);
    }
  };

  return (
    // No close on a background click: this panel is full of typed-in text, and a
    // stray click outside the card used to throw it away with no warning
    // (Christian Gravias, 24 Aug 2026). Closing is the X, or Cancel.
    <div
      className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center bg-black/40 p-3 overflow-y-auto"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-xl w-full max-w-lg my-6 flex flex-col max-h-[90vh]"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-[#2D3441] flex items-center gap-2">
            <Send className="w-4 h-4 text-[#55C5D0]" />
            Send Lead To An Agent
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close hand-over"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-gray-600">
            <div className="font-bold text-gray-900 text-sm">{leadName}</div>
            <div className="mt-0.5">
              {[lead.phone, lead.email].filter(Boolean).join(" · ") || "No contact details"}
            </div>
            {(lead.property_address || lead.owner_subject_address) && (
              <div className="mt-0.5">{lead.property_address || lead.owner_subject_address}</div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1" htmlFor="assign-lead-agent">
              Agent
            </label>
            <InfiniteScrollSelect
              value={agentId}
              onChange={(val) => setAgentId(val)}
              placeholder="Unassigned"
              fetchOptions={fetchAgentOptions}
              initialOptions={agents}
              getLabel={(a) => a.name || a.email}
              getValue={(a) => a.id}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1" htmlFor="assign-lead-note">
              Note to the agent <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              id="assign-lead-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              disabled={isUnassigning}
              placeholder="Came off the Cheltenham campaign. Ring her before 5, she is only free today."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0] disabled:bg-gray-50"
            />
            <p className="mt-1 text-[11px] text-gray-400">
              {isUnassigning
                ? "Pick an agent to write them a note."
                : isSameAgent
                  ? "This lead is already with this agent, so no email is sent. The note is still saved to the lead's notes."
                  : "Goes at the top of the email the agent gets, and is saved to the lead's notes."}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-[#55C5D0] hover:bg-[#45b5c0] disabled:opacity-60 text-white px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-2"
          >
            <Send className="w-3.5 h-3.5" />
            {saving ? "Sending..." : isUnassigning ? "Unassign" : "Send Lead"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AssignLeadModal;
